import React, { useMemo, useState } from 'react';
import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/api';
import DocumentCreateModal from './DocumentCreateModal';
import { CategoryTag } from '../common/CategoryTag';

const { Text } = Typography;

type DocumentEntityType = 'location' | 'equipment' | 'part' | 'supplier' | 'user';

const entityTypeLabels: Record<DocumentEntityType, string> = {
  location: 'エリア',
  equipment: '設備',
  part: '部品',
  supplier: '業者',
  user: 'ユーザー',
};

type DocumentListItem = {
  document: {
    id: string;
    title: string;
    category: string;
    createdAt?: string | null;
    updatedAt?: string | null;
    deletedAt?: string | null;
  };
  documentType?: {
    id: string;
    name: string;
    category?: string | null;
    color?: string | null;
    icon?: string | null;
  } | null;
  association?: {
    entityType?: string | null;
    entityId?: string | null;
  } | null;
  associationLocation?: {
    id?: string | null;
    name?: string | null;
  } | null;
  associationEquipment?: {
    id?: string | null;
    name?: string | null;
    displayId?: string | null;
  } | null;
  associationPart?: {
    id?: string | null;
    name?: string | null;
    displayId?: string | null;
  } | null;
  associationSupplier?: {
    id?: string | null;
    name?: string | null;
  } | null;
  associationUser?: {
    id?: string | null;
    name?: string | null;
    email?: string | null;
  } | null;
  createdByUser?: {
    id?: string;
    name?: string | null;
    email?: string | null;
  } | null;
  updatedByUser?: {
    id?: string;
    name?: string | null;
    email?: string | null;
  } | null;
};

interface DocumentListSectionProps {
  entityType: DocumentEntityType;
  entityId: string;
  title?: string;
  allowCreate?: boolean;
  allowOfficial?: boolean;
  variant?: 'card' | 'plain';
}

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ja-JP');
};

const formatUser = (user?: { name?: string | null; email?: string | null } | null) => {
  if (!user) return '-';
  return user.name || user.email || '-';
};

const resolveAssociationName = (record: DocumentListItem) => {
  const association = record.association;
  if (!association?.entityType) return null;
  switch (association.entityType) {
    case 'location':
      return record.associationLocation?.name ?? null;
    case 'equipment': {
      const name = record.associationEquipment?.name ?? null;
      const displayId = record.associationEquipment?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    case 'part': {
      const name = record.associationPart?.name ?? null;
      const displayId = record.associationPart?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    case 'supplier':
      return record.associationSupplier?.name ?? null;
    case 'user':
      return record.associationUser?.name ?? record.associationUser?.email ?? null;
    default:
      return null;
  }
};

export const DocumentListSection: React.FC<DocumentListSectionProps> = ({
  entityType,
  entityId,
  title = '書類',
  allowCreate = true,
  allowOfficial = false,
  variant = 'card',
}) => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [createOpen, setCreateOpen] = useState(false);

  const listInput = useMemo(
    () => ({
      entityType,
      entityId,
      page: 1,
      limit: 50,
    }),
    [entityType, entityId]
  );

  const { data, isLoading } = trpc.document.list.useQuery(listInput, {
    enabled: Boolean(entityId),
  });

  const rows: DocumentListItem[] = (data?.items ?? []) as DocumentListItem[];

  const columns: ColumnsType<DocumentListItem> = [
    {
      title: '書類名',
      dataIndex: ['document', 'title'],
      key: 'title',
      render: (value: string, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/documents/${record.document.id}`)}
          style={{ padding: 0 }}
        >
          {value}
        </Button>
      ),
    },
    {
      title: '書類カテゴリ',
      key: 'type',
      render: (_: unknown, record) => (
        <CategoryTag
          category={record.documentType ?? null}
          linkPath="/app/documents"
        />
      ),
    },
    {
      title: '区分',
      dataIndex: ['document', 'category'],
      key: 'category',
      render: (value: string) => (
        <Tag color={value === 'official' ? 'red' : 'default'}>
          {value === 'official' ? '法定点検' : '一般'}
        </Tag>
      ),
    },
    {
      title: '関連',
      key: 'association',
      render: (_: unknown, record) => {
        const association = record.association;
        if (!association?.entityType) {
          return '-';
        }
        const typeLabel = entityTypeLabels[association.entityType as DocumentEntityType]
          ?? association.entityType;
        const name = resolveAssociationName(record);
        return name ? `${typeLabel} / ${name}` : typeLabel;
      },
    },
    {
      title: '作成者',
      key: 'createdBy',
      render: (_: unknown, record) => formatUser(record.createdByUser),
    },
    {
      title: '作成日',
      dataIndex: ['document', 'createdAt'],
      key: 'createdAt',
      render: (value?: string | null) => formatDate(value),
    },
  ];

  const content = (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Text strong>{title}</Text>
          <Tag>{entityTypeLabels[entityType]}</Tag>
        </Space>
        {allowCreate && (
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            書類登録
          </Button>
        )}
      </Space>
      <Table
        rowKey={(record) => record.document.id}
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: '関連する書類はありません' }}
      />
      <DocumentCreateModal
        open={createOpen}
        entityType={entityType}
        entityId={entityId}
        allowOfficial={allowOfficial}
        onCancel={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          void utils.document.list.invalidate(listInput);
        }}
      />
    </Space>
  );

  if (variant === 'plain') {
    return content;
  }

  return (
    <Card>
      {content}
    </Card>
  );
};

export default DocumentListSection;
